import { useEffect, useState } from "react";
import { AdminShell } from "@/components/Layout";
import { api, formatApiErrorDetail } from "@/lib/api";
import { toast } from "sonner";
import { ShieldCheck, Warning, CheckCircle, Clock, X, Sparkle, Copy, Buildings } from "@phosphor-icons/react";

const STATUS_COLORS = {
  compliant: "bg-emerald-100 text-emerald-900 border-emerald-500",
  missing: "bg-red-100 text-red-900 border-red-500",
  expired: "bg-red-100 text-red-900 border-red-500",
  expiring_soon: "bg-amber-100 text-amber-900 border-amber-500",
  unknown: "bg-slate-100 text-slate-500 border-slate-300",
  na: "bg-slate-50 text-slate-400 border-slate-200",
};

export default function PhillyComplianceRadar() {
  const [summary, setSummary] = useState(null);
  const [records, setRecords] = useState([]);
  const [expiringSoon, setExpiringSoon] = useState([]);
  const [busy, setBusy] = useState(false);
  const [editing, setEditing] = useState(null); // {property_address, laws}
  const [drafting, setDrafting] = useState(null); // notice modal

  const load = async () => {
    setBusy(true);
    try {
      const [s, r, e] = await Promise.all([
        api.get("/philly-radar/summary"),
        api.get("/philly-radar/records"),
        api.get("/philly-radar/expiring-soon?days=60"),
      ]);
      setSummary(s.data); setRecords(r.data || []); setExpiringSoon(e.data.items || []);
    } catch (er) { toast.error("Failed to load radar"); }
    finally { setBusy(false); }
  };
  useEffect(() => { load(); }, []);

  const laws = summary?.laws || [];

  return (
    <AdminShell>
      <div className="space-y-6" data-testid="philly-radar-page">
        <div className="flex items-start justify-between gap-4 flex-wrap">
          <div>
            <h1 className="text-4xl font-black uppercase tracking-widest flex items-center gap-2">
              <ShieldCheck weight="fill" className="text-red-500"/> Philly Compliance Radar
            </h1>
            <p className="text-sm text-slate-600 mt-2 max-w-2xl">
              Per-property tracker for Philadelphia rental laws — Good Cause Eviction, 90-day rent notices,
              CRS, Lead-Safe Cert, Screening Reform, Cash-payment ban. Draft compliant notices with AI.
            </p>
          </div>
          <button onClick={() => setDrafting({})} data-testid="draft-notice-btn"
            className="h-11 px-4 bg-red-600 hover:bg-red-700 text-white font-black uppercase tracking-widest text-sm shadow-[3px_3px_0_#0f172a] inline-flex items-center gap-2">
            <Sparkle weight="fill" size={14}/> Draft AI Notice
          </button>
        </div>

        {summary && (
          <div className="grid grid-cols-2 sm:grid-cols-5 gap-3">
            <Stat label="Properties" value={summary.counters.total} tone="slate" testid="stat-total"/>
            <Stat label="Fully Compliant" value={summary.counters.compliant} tone="emerald" testid="stat-compliant"/>
            <Stat label="Any Missing" value={summary.counters.any_missing} tone="red" testid="stat-missing"/>
            <Stat label="Any Expired" value={summary.counters.any_expired} tone="red" testid="stat-expired"/>
            <Stat label="Expiring Soon" value={summary.counters.any_expiring_soon} tone="amber" testid="stat-soon"/>
          </div>
        )}

        {expiringSoon.length > 0 && (
          <div className="rugged-card p-4 border-4 border-amber-500 bg-amber-50" data-testid="expiring-alert">
            <div className="font-black uppercase tracking-widest text-amber-900 flex items-center gap-2 mb-2">
              <Clock size={16}/> {expiringSoon.length} expiring in next 60 days
            </div>
            <div className="space-y-1 max-h-40 overflow-y-auto text-xs">
              {expiringSoon.slice(0, 20).map((e, i) => (
                <div key={i} className="flex items-center justify-between border-b border-amber-200 py-1">
                  <div><b>{e.address}</b> · {e.law_label}</div>
                  <div className="font-black text-amber-900">
                    {e.days_until === 0 ? "TODAY" : `${e.days_until}d`}
                  </div>
                </div>
              ))}
            </div>
          </div>
        )}

        <div>
          <h2 className="text-lg font-black uppercase tracking-widest mb-3">Property compliance grid</h2>
          {busy && <div className="text-slate-500 text-sm">Loading…</div>}
          {!busy && records.length === 0 && (
            <div className="p-8 border-4 border-slate-300 bg-slate-50 text-center">
              <Buildings size={40} className="mx-auto text-slate-400"/>
              <div className="mt-2 font-black uppercase tracking-widest text-slate-700">No records yet</div>
              <div className="text-xs text-slate-500 mt-1">Click a property from the property list to start a record.</div>
            </div>
          )}
          <div className="space-y-2">
            {records.map(r => (
              <div key={r.address_key} className="rugged-card p-3" data-testid={`record-${r.address_key}`}>
                <div className="flex items-center justify-between mb-2 flex-wrap gap-2">
                  <div className="font-black text-sm">{r.property_address}</div>
                  <button onClick={() => setEditing(r)} data-testid={`edit-${r.address_key}`}
                    className="h-8 px-2 border-2 border-slate-900 font-black uppercase tracking-widest text-[10px] hover:bg-slate-100">
                    Edit
                  </button>
                </div>
                <div className="flex flex-wrap gap-1">
                  {laws.map(law => {
                    const l = (r.laws || []).find(x => x.key === law.key);
                    const status = l?.status || "unknown";
                    return (
                      <span key={law.key} title={law.label + (l?.expires ? ` · exp ${l.expires}` : "")}
                        className={`px-2 h-6 leading-6 text-[9px] font-black uppercase tracking-widest border-2 ${STATUS_COLORS[status] || STATUS_COLORS.unknown}`}>
                        {law.label.split(' ').slice(0, 2).join(' ')} · {status.replace('_', ' ')}
                      </span>
                    );
                  })}
                </div>
              </div>
            ))}
          </div>
        </div>
      </div>

      {editing && (
        <RecordEditor
          record={editing}
          laws={laws}
          onClose={() => setEditing(null)}
          onSaved={() => { setEditing(null); load(); }}
        />
      )}
      {drafting !== null && (
        <NoticeDrafter
          onClose={() => setDrafting(null)}
        />
      )}
    </AdminShell>
  );
}

function Stat({ label, value, tone, testid }) {
  const bg = { slate: "bg-slate-100", emerald: "bg-emerald-100", red: "bg-red-100", amber: "bg-amber-100" }[tone] || "bg-slate-100";
  const border = { slate: "border-slate-400", emerald: "border-emerald-500", red: "border-red-500", amber: "border-amber-500" }[tone] || "border-slate-400";
  return (
    <div className={`p-3 border-4 ${border} ${bg}`} data-testid={testid}>
      <div className="text-[10px] font-black uppercase tracking-widest opacity-70">{label}</div>
      <div className="text-3xl font-black mt-1">{value}</div>
    </div>
  );
}

function RecordEditor({ record, laws, onClose, onSaved }) {
  const [address, setAddress] = useState(record.property_address || "");
  const [rows, setRows] = useState(() => {
    const existing = record.laws || [];
    return laws.map(law => {
      const e = existing.find(x => x.key === law.key) || {};
      return { key: law.key, label: law.label, status: e.status || "unknown", expires: e.expires || "", note: e.note || "" };
    });
  });
  const [busy, setBusy] = useState(false);

  const save = async () => {
    setBusy(true);
    try {
      await api.put(`/philly-radar/records/${encodeURIComponent((record.address_key || address).toLowerCase())}`, {
        property_address: address,
        laws: rows.map(r => ({ key: r.key, status: r.status, expires: r.expires, note: r.note })),
      });
      toast.success("Saved");
      onSaved();
    } catch (e) { toast.error(formatApiErrorDetail(e?.response?.data?.detail ?? "Save failed")); }
    finally { setBusy(false); }
  };

  return (
    <div className="fixed inset-0 bg-slate-900/60 z-[80] flex items-end sm:items-center justify-center p-2 sm:p-4" data-testid="record-editor">
      <div className="bg-white w-full max-w-2xl max-h-[92vh] overflow-y-auto border-4 border-slate-900 shadow-[6px_6px_0_#0f172a]">
        <div className="p-4 bg-red-600 text-white flex items-start justify-between sticky top-0">
          <div>
            <div className="text-[10px] font-black uppercase tracking-widest opacity-80">Compliance Record</div>
            <div className="text-lg font-black uppercase tracking-widest">{address || "New Property"}</div>
          </div>
          <button onClick={onClose} className="p-1 hover:bg-red-700"><X size={18}/></button>
        </div>
        <div className="p-4 space-y-3">
          {!record.address_key && (
            <input list="known-addresses" autoComplete="off" value={address} onChange={e => setAddress(e.target.value)} placeholder="Property address"
              className="w-full h-11 px-3 border-2 border-slate-300 text-sm"/>
          )}
          <div className="space-y-2">
            {rows.map((r, i) => (
              <div key={r.key} className="p-2 border-2 border-slate-200 space-y-1" data-testid={`law-${r.key}`}>
                <div className="font-black text-xs">{r.label}</div>
                <div className="grid grid-cols-3 gap-2">
                  <select value={r.status} onChange={e => setRows(rs => rs.map((x, j) => j === i ? {...x, status: e.target.value} : x))}
                    data-testid={`law-status-${r.key}`}
                    className="h-9 px-2 border-2 border-slate-300 text-xs">
                    {["unknown", "compliant", "missing", "expired", "expiring_soon", "na"].map(s => (
                      <option key={s} value={s}>{s.replace('_', ' ')}</option>
                    ))}
                  </select>
                  <input value={r.expires} onChange={e => setRows(rs => rs.map((x, j) => j === i ? {...x, expires: e.target.value} : x))}
                    placeholder="Expires YYYY-MM-DD" className="h-9 px-2 border-2 border-slate-300 text-xs"/>
                  <input value={r.note} onChange={e => setRows(rs => rs.map((x, j) => j === i ? {...x, note: e.target.value} : x))}
                    placeholder="Note" className="h-9 px-2 border-2 border-slate-300 text-xs"/>
                </div>
              </div>
            ))}
          </div>
          <button onClick={save} disabled={busy} data-testid="record-save"
            className="w-full h-12 bg-red-600 hover:bg-red-700 text-white font-black uppercase tracking-widest text-sm disabled:opacity-50 shadow-[3px_3px_0_#0f172a]">
            {busy ? "Saving…" : "Save Compliance Record"}
          </button>
        </div>
      </div>
    </div>
  );
}

function NoticeDrafter({ onClose }) {
  const [address, setAddress] = useState("");
  const [tenantName, setTenantName] = useState("");
  const [kind, setKind] = useState("rent_increase_90day");
  const [context, setContext] = useState("");
  const [result, setResult] = useState(null);
  const [busy, setBusy] = useState(false);

  const generate = async () => {
    setBusy(true);
    try {
      const { data } = await api.post("/philly-radar/generate-notice", {
        property_address: address, tenant_name: tenantName, kind, context,
      }, { timeout: 60000 });
      setResult(data);
    } catch (e) { toast.error(formatApiErrorDetail(e?.response?.data?.detail ?? "Draft failed")); }
    finally { setBusy(false); }
  };

  const copyAll = async () => {
    if (!result) return;
    await navigator.clipboard.writeText(`Subject: ${result.subject}\n\n${result.body}`);
    toast.success("Copied");
  };

  return (
    <div className="fixed inset-0 bg-slate-900/60 z-[80] flex items-end sm:items-center justify-center p-2 sm:p-4" data-testid="notice-drafter">
      <div className="bg-white w-full max-w-2xl max-h-[92vh] overflow-y-auto border-4 border-slate-900 shadow-[6px_6px_0_#0f172a]">
        <div className="p-4 bg-red-600 text-white flex items-start justify-between sticky top-0">
          <div>
            <div className="text-[10px] font-black uppercase tracking-widest opacity-80">AI Notice Generator</div>
            <div className="text-lg font-black uppercase tracking-widest">Draft a compliant notice</div>
          </div>
          <button onClick={onClose} className="p-1 hover:bg-red-700"><X size={18}/></button>
        </div>
        <div className="p-4 space-y-3">
          <select value={kind} onChange={e => setKind(e.target.value)} data-testid="notice-kind"
            className="w-full h-10 px-2 border-2 border-slate-300 text-sm">
            <option value="rent_increase_90day">90-day rent increase notice</option>
            <option value="good_cause_nonrenewal">Good Cause / Just Cause non-renewal</option>
            <option value="lease_termination">Lease termination</option>
            <option value="crs_request">CRS cover letter</option>
          </select>
          <input list="known-addresses" autoComplete="off" value={address} onChange={e => setAddress(e.target.value)} placeholder="Property address" data-testid="notice-address"
            className="w-full h-10 px-3 border-2 border-slate-300 text-sm"/>
          <input value={tenantName} onChange={e => setTenantName(e.target.value)} placeholder="Tenant name" data-testid="notice-tenant"
            className="w-full h-10 px-3 border-2 border-slate-300 text-sm"/>
          <textarea value={context} onChange={e => setContext(e.target.value)} rows={3} data-testid="notice-context"
            placeholder="Required — facts to include (new rent amount, effective date, just cause reason, etc.)"
            className={`w-full p-3 border-2 text-sm ${context.trim() ? "border-slate-300" : "border-amber-400 bg-amber-50"}`}/>
          {!context.trim() && (
            <div className="text-[11px] text-amber-800 font-medium -mt-1">
              ⚠ Add specific facts above — the AI needs them to draft anything useful.
              (New rent amount, effective date, just-cause reason, etc.)
            </div>
          )}
          <button onClick={generate} disabled={busy || !address || !context.trim()} data-testid="notice-generate"
            className="w-full h-11 bg-red-600 hover:bg-red-700 text-white font-black uppercase tracking-widest text-sm disabled:opacity-50 shadow-[3px_3px_0_#0f172a]">
            {busy ? "Claude drafting…" : "Generate Notice"}
          </button>
          {result && (
            <div className="p-3 border-4 border-slate-900 bg-slate-50 space-y-2" data-testid="notice-result">
              <div className="font-black text-sm">{result.subject}</div>
              {result.body && result.body.trim().length > 20 ? (
                <pre className="text-xs whitespace-pre-wrap font-mono max-h-64 overflow-y-auto">{result.body}</pre>
              ) : (
                <div className="p-3 border-2 border-amber-500 bg-amber-50 text-amber-900 text-xs" data-testid="notice-empty-warning">
                  <div className="font-black uppercase tracking-widest text-[10px] mb-1">⚠ Empty draft</div>
                  The AI didn't return usable notice text. Please add specific facts in the field above (new rent amount, effective date, just-cause reason, etc.) and click Generate again.
                </div>
              )}
              <div className="text-[10px] text-slate-500 italic">{result.disclaimer}</div>
              <button onClick={copyAll} disabled={!result.body || result.body.trim().length <= 20} data-testid="notice-copy"
                className="w-full h-10 border-2 border-slate-900 hover:bg-slate-100 font-black uppercase tracking-widest text-xs inline-flex items-center justify-center gap-1 disabled:opacity-40">
                <Copy size={12}/> Copy to Clipboard
              </button>
            </div>
          )}
        </div>
      </div>
    </div>
  );
}
